import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../../hooks/useNavigateWithScrollMemory';
import { Link as RouterLink } from 'react-router-dom';
import { REFERRER_TYPE } from '../../../constants/gaEnums';
import theme from '../../../theme';
import CategoryTag from '../components/CategoryTag';
import CardTitle from '../../../components/common/CardTitle';
import AlbumTitleLink from '../../../components/common/AlbumTitleLink';

const lineClampSx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: '1.5',
};

const userLinkStyle = { fontWeight: 'bold', color: 'inherit', textDecoration: 'none' };

// 게시물 URL (제목 클릭)
const getArticleUrl = (data) => {
  switch (data.category) {
    case 'Albums':
      return data.album?.slug
        ? `/reviews/${data.album.slug}`
        : `/reviews/${data.album?.albumSource?.toLowerCase()}/${data.album?.sourceId}`;
    case 'Reviews': {
      const albumBase = data.album?.slug
        ? `/reviews/${data.album.slug}`
        : `/reviews/${data.album?.albumSource?.toLowerCase()}/${data.album?.sourceId}`;
      return `${albumBase}/user-reviews/${data.review?.id}`;
    }
    case 'Lists':
      return `/lists/${data.list?.slug || data.list?.id}`;
    case 'News':
      return `/news/${data.news?.slug || data.news?.id}`;
    case 'Notice':
      return `/notice/${data.notice?.slug || data.notice?.id}`;
    default:
      return '/';
  }
};

// 댓글 위치 URL (댓글 내용 클릭)
const getCommentUrl = (data) => `${getArticleUrl(data)}/comment/${data.comment?.id}`;

const CommentItem = ({ data }) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();

  const articleUrl = getArticleUrl(data);
  const commentUrl = getCommentUrl(data);

  const handleNavigate = (url) => (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(url, { state: { referrerType: REFERRER_TYPE.MYPAGE } });
    }
  };

  return (
    <Box>
      <CategoryTag category={data.category} sx={{ mb: { xs: '12px', lg: '16px' } }} />

      {/* 제목 영역 */}
      {data.category === 'Albums' && (
        <Typography
          fontSize={{ xs: 13, md: 14, lg: 16 }}
          color={theme.palette.black[6]}
          sx={lineClampSx}
        >
          <RouterLink to={articleUrl} onClick={handleNavigate(articleUrl)} style={userLinkStyle}>
            {data.artist?.name}
            {' - '}
            <AlbumTitleLink
              title={data.album?.title}
              contentRating={data.album?.contentRating}
              inline
              iconSx={{ width: '12px', height: '12px' }}
            />
          </RouterLink>
        </Typography>
      )}

      {data.category === 'Reviews' && (
        <Typography
          fontSize={{ xs: 13, md: 14, lg: 16 }}
          color={theme.palette.black[6]}
          sx={lineClampSx}
        >
          <RouterLink
            to={`/users/${encodeURIComponent(data.user?.nickname)}`}
            onClick={handleNavigate(`/users/${encodeURIComponent(data.user?.nickname)}`)}
            style={userLinkStyle}
          >
            {data.user?.nickname}
          </RouterLink>
          {'님의 리뷰 '}
          <RouterLink to={articleUrl} onClick={handleNavigate(articleUrl)} style={userLinkStyle}>
            {data.artist?.name}
            {' - '}
            <AlbumTitleLink
              title={data.album?.title}
              contentRating={data.album?.contentRating}
              inline
              iconSx={{ width: '12px', height: '12px' }}
            />
          </RouterLink>
        </Typography>
      )}

      {data.category === 'Lists' && (
        <Typography
          fontSize={{ xs: 13, md: 14, lg: 16 }}
          color={theme.palette.black[6]}
          sx={lineClampSx}
        >
          <RouterLink
            to={`/users/${encodeURIComponent(data.user?.nickname)}`}
            onClick={handleNavigate(`/users/${encodeURIComponent(data.user?.nickname)}`)}
            style={userLinkStyle}
          >
            {data.user?.nickname}
          </RouterLink>
          {'님의 플리 '}
          <CardTitle
            title={data.list?.title}
            to={articleUrl}
            onClick={handleNavigate(articleUrl)}
            inline
            sx={{ fontSize: { xs: 13, md: 14, lg: 16 } }}
          />
        </Typography>
      )}

      {data.category === 'News' && (
        <CardTitle
          title={data.news?.title}
          to={articleUrl}
          onClick={handleNavigate(articleUrl)}
          sx={{ fontSize: { xs: 13, md: 14, lg: 16 }, color: theme.palette.black[6] }}
        />
      )}

      {data.category === 'Notice' && (
        <CardTitle
          title={data.notice?.title}
          to={articleUrl}
          onClick={handleNavigate(articleUrl)}
          sx={{ fontSize: { xs: 13, md: 14, lg: 16 }, color: theme.palette.black[6] }}
        />
      )}

      {/* 댓글 내용 */}
      <Typography
        component={RouterLink}
        to={commentUrl}
        state={{ referrerType: REFERRER_TYPE.MYPAGE }}
        onClick={handleNavigate(commentUrl)}
        fontSize={{ xs: 14, md: 13, lg: 14 }}
        color={theme.palette.black[6]}
        sx={{ textDecoration: 'none', mt: { xs: '12px', md: '16px' }, ...lineClampSx }}
      >
        {data.comment?.content}
      </Typography>

      {/* 수정일 */}
      <Typography
        fontSize={{ xs: 12, md: 13, lg: 14 }}
        color={theme.palette.black[2]}
        sx={{ mt: { xs: '12px', md: '16px' } }}
      >
        {data.comment?.updatedAt}
      </Typography>
    </Box>
  );
};

export default CommentItem;
